/**
 * The same period one year earlier, for Perioder's comparison period (#861).
 *
 * A range inside one ISO week starting on its Monday maps to the same ISO week
 * number last year, a range inside one month starting on the 1st to the same
 * month, and anything else to the same dates with 29 February falling back to
 * the 28th. Dates are YYYY-MM-DD strings and
 * all arithmetic is in UTC, so the result never depends on the browser's zone.
 */

const DAY_MS = 86_400_000

const parse = (iso: string) => new Date(`${iso}T00:00:00Z`)
const fmt = (d: Date) => d.toISOString().slice(0, 10)
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS)
const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

/** Monday of ISO week 1 of `isoYear` (the week holding 4 January). */
const isoWeekOneMonday = (isoYear: number) => {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4))
  return addDays(jan4, -((jan4.getUTCDay() + 6) % 7))
}

/** [ISO year, ISO week] of a date. */
const isoWeek = (d: Date): [number, number] => {
  const thursday = addDays(d, 3 - ((d.getUTCDay() + 6) % 7))
  const year = thursday.getUTCFullYear()
  return [year, Math.floor((thursday.getTime() - isoWeekOneMonday(year).getTime()) / (7 * DAY_MS)) + 1]
}

const minusYear = (d: Date) => {
  const year = d.getUTCFullYear() - 1
  const day = Math.min(d.getUTCDate(), daysInMonth(year, d.getUTCMonth()))
  return new Date(Date.UTC(year, d.getUTCMonth(), day))
}

export function samePeriodLastYear(start: string, end: string): { start: string; end: string } {
  const s = parse(start)
  const e = parse(end)

  // One ISO week from its Monday (end capped at the data date or not).
  const span = Math.round((e.getTime() - s.getTime()) / DAY_MS)
  if (s.getUTCDay() === 1 && span >= 0 && span <= 6) {
    const [year, week] = isoWeek(s)
    // Week 53 has no counterpart in a 52-week year; use its last week.
    const lastWeek = isoWeek(addDays(isoWeekOneMonday(year), -7))[1]
    const monday = addDays(isoWeekOneMonday(year - 1), 7 * (Math.min(week, lastWeek) - 1))
    return { start: fmt(monday), end: fmt(addDays(monday, span)) }
  }

  // One calendar month from the 1st; a month-end stays a month-end.
  if (s.getUTCDate() === 1 && s.getUTCFullYear() === e.getUTCFullYear() && s.getUTCMonth() === e.getUTCMonth()) {
    const year = s.getUTCFullYear() - 1
    const month = s.getUTCMonth()
    const last = daysInMonth(year, month)
    const isMonthEnd = e.getUTCDate() === daysInMonth(e.getUTCFullYear(), month)
    return {
      start: fmt(new Date(Date.UTC(year, month, 1))),
      end: fmt(new Date(Date.UTC(year, month, isMonthEnd ? last : Math.min(e.getUTCDate(), last)))),
    }
  }

  return { start: fmt(minusYear(s)), end: fmt(minusYear(e)) }
}
